/**
 * "Message info" — V6-05 §4.
 *
 * Who has read your message and who merely has it, with times. The times are
 * each person's *mark* ("everything up to here"), not the moment they looked at
 * this particular line — because that moment was never recorded, and inventing
 * a precision the data does not have is how a receipt stops being believed.
 */

import { UserAvatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMessageInfo } from './api';

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MessageInfoDialog({
  messageId,
  onClose,
}: {
  messageId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useMessageInfo(messageId);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Message info"
      data-testid="message-info"
      className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="vt-surface vt-elevated flex w-[320px] flex-col gap-2 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[13px] font-medium">Message info</h3>

        {isLoading ? (
          <Skeleton className="h-24" />
        ) : (data?.rows ?? []).length === 0 ? (
          <p className="text-[12px] vt-muted">Nobody else is in this conversation.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data!.rows.map((row) => (
              <li
                key={row.userId}
                className="flex items-center gap-2 text-[12px]"
                data-testid="message-info-row"
                data-user-id={row.userId}
                data-read={row.readAt ? 'true' : 'false'}
              >
                <UserAvatar
                  id={row.userId}
                  name={row.name}
                  src={row.avatarUrl ?? undefined}
                  size={22}
                />
                <span className="min-w-0 flex-1 truncate">{row.name}</span>
                <span className="vt-mono vt-muted" title="Read">
                  {row.readAt ? when(row.readAt) : row.deliveredAt ? 'delivered' : 'sent'}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Button variant="secondary" size="sm" data-testid="message-info-close" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
